import COLLECTION_CONTENT_PAGE_QUERY from 'Root/Pages/CollectionContent/collection.graphql'

import {
  CollectionContents,
  CollectionContents_contents_edges,
  CollectionPageContents_collection_contents_edges_node,
  Visibility,
} from 'globalTypes'
import { NextPage } from 'next'
import type { GetServerSideProps } from 'next'
import { NextSeo } from 'next-seo'
import requestIp from 'request-ip'

import { Provider } from 'Root/Contexts/Ip'
import CollectionContent from 'Root/Pages/CollectionContent'
import VisitTracker from 'Root/Pages/Content/Component/VisitTracker'
import apolloClient from 'lib/apollo-client'
import canAccessContent from 'lib/can-access-content'
import getElementServerSideProps from 'lib/get-element-server-side-props'
import getQueryString from 'lib/get-query-string'
import { handleNotFoundOnMissingDataOrAuthentication } from 'lib/handle-not-found-on-missing-data-or-authentication'
import { loadCatalog } from 'lib/i18n'
import { getSubjectFromJwt } from 'lib/subject-from-jwt'
import filter from 'lodash/filter'
import map from 'lodash/map'

import { defaultFavicons } from '../../../../../next-seo.config'

interface Props {
  content: CollectionPageContents_collection_contents_edges_node & {
    visibilityForCollection: Visibility
    canManage: boolean
  }
  collection: any
  hasUser: boolean
  ip: string | null
}

const DefaultSeo = ({ content, brandName, brandLogo }) => {
  if (!content) return null

  const images = map(filter([content.cover]), (url) => ({
    url,
  }))

  const pageFavicon = brandLogo
    ? [
        {
          rel: 'icon',
          href: brandLogo,
        },
      ]
    : defaultFavicons

  return (
    <NextSeo
      additionalLinkTags={pageFavicon}
      additionalMetaTags={[
        {
          name: 'msapplication-TileColor',
          content: '#ffffff',
        },
        {
          name: 'theme-color',
          content: '#ffffff',
        },
      ]}
      title={`${content.title} – ${brandName ?? 'Taylora'}`}
      description={content.description}
      openGraph={{
        images,
      }}
    />
  )
}

const Page: NextPage<Props> = (props: Props) => {
  const content = props.content

  return (
    <Provider value={props.ip}>
      <DefaultSeo
        content={content}
        brandName={props.collection?.creator?.workspace?.brandName}
        brandLogo={props.collection?.creator?.workspace?.brandLogoSmall}
      />
      <VisitTracker id={content.id} />
      <CollectionContent ssContent={content} ssCollection={props.collection} />
    </Provider>
  )
}

const getContentInCollection = (
  content_id: string,
  contents: CollectionContents_contents_edges[]
) => {
  return contents.find(({ node: content }) => content.slug === content_id)
}

const checkContentInCollection = (collection, content_identifier) => {
  let content = getContentInCollection(
    content_identifier,
    collection.contents.edges
  )

  if (!content) {
    const { edges } = collection.modules
    for (const mod of edges) {
      content = getContentInCollection(
        content_identifier,
        mod.node.contents.edges
      )

      if (content) return content
    }
  }

  return content
}

interface QueryData {
  collection: CollectionContents
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { identifier, token, authorizationToken, content_identifier } =
    getElementServerSideProps(context)

  const translation = await loadCatalog(getQueryString(context.query.locale))

  const { data } = await apolloClient.query<QueryData>({
    query: COLLECTION_CONTENT_PAGE_QUERY,
    variables: {
      identifier: identifier,
      token: authorizationToken,
      userId: getSubjectFromJwt(token),
    },
    context: {
      headers: {
        Authorization: Boolean(token) ? `Bearer ${token}` : undefined,
      },
    },
  })

  const collection = data.collection

  if (!collection) {
    return handleNotFoundOnMissingDataOrAuthentication(context.req.url, token)
  }

  //... if the collection is on sale and this content identifier exists in it, don't show 404 page
  const contentNode = checkContentInCollection(collection, content_identifier)

  if (!contentNode) {
    return handleNotFoundOnMissingDataOrAuthentication(context.req.url, token)
  }

  const { node: content } = contentNode

  if (
    !canAccessContent(
      content.visibilityForCollection,
      content.canManage,
      collection.visibility,
      collection.canRead
    )
  ) {
    return handleNotFoundOnMissingDataOrAuthentication(context.req.url, token)
  }

  return {
    props: {
      content: content,
      collection: collection,
      hasUser: Boolean(token),
      ip: requestIp.getClientIp(context.req),
      translation: translation,
    } as Props,
  }
}

export default Page
